// Import JS Modules
import flatMap from 'lodash/flatMap';
import cloneDeep from 'lodash.clonedeep';
import jsonDataURL from './json-data-url';
import parseQueryString from '../../_utils/parse-query-string';
import { builderDispatch, WebUI } from '../../_utils/ui';
import {
  UpdatePhotoEvents,
  RemoveSizeEvents,
  DefaultsEvents,
} from './events/event-declarations';
import genericHelpers from '../../_utils/generic-helpers';
import resetPhotoEdits from './reset-photo-edits';
import SizeHelpers from './size-helpers';
import PhotoModifications from './photo-modifications';
import ViewingSizeKey from '../mobile/viewing-size-key';

// This file contains functions that are common to the prints builder and the mobile prints builder.
// Any change made to these functions have to be tested in the mobile component.

const PHOTO_ATTRIBUTES = [
  'angle',
  'crop',
  'fx',
  'saturation',
  'colour_fx',
  'straighten',
  'gamma',
  'quantity',
];

const PrintsBuilderMixin = {
  data: {
    finishes: [],
    delivery_options: [],
  },
  loadCallbacks() {
    this.builderDispatchToken = builderDispatch.register(payload => {
      switch (payload.type) {
        case WebUI.UPLOAD_BEGUN:
          this.handleUploadBegun(payload.number_of_files);
          break;
        case WebUI.UPLOAD_PROGRESS:
          this.handleUploadProgress(
            payload.data,
            payload.source,
            payload.number_of_files
          );
          break;
        case WebUI.UPLOAD_PAUSED:
          this.handleUploadPaused();
          break;
        case WebUI.UPLOAD_RESUMED:
          this.handleUploadResumed();
          break;
        case WebUI.UPLOAD_DONE:
          this.handleUploadDone(
            payload.number_of_files,
            payload.number_of_errors,
            payload.message
          );
          break;
        case WebUI.UPLOAD_ABORTED:
          this.handleAbortedUpload();
          break;
        case WebUI.UPLOAD_CLOSED:
          this.handleUploadClosed();
          break;
        case UpdatePhotoEvents.UPDATE:
          this.handleApplyEditPhoto(payload.photo, payload.hard);
          break;
        case UpdatePhotoEvents.REMOVE:
          if (payload.useRoot) this.handleRemovePhotoByRoot(payload.imageId);
          else {
            this.handleRemovePhoto(
              payload.sizeKey,
              payload.imageId,
              payload.selectedPhotoId
            );
          }
          break;
        case UpdatePhotoEvents.ADD_TO_SIZE:
          this.handleAddPhotoToSize(
            payload.size_key,
            payload.root_image_id,
            payload.targetSizeKey,
            payload.quantity
          );
          break;
        case UpdatePhotoEvents.RESIZE:
          this.handleResizePhoto(
            payload.size_key,
            payload.image_id,
            payload.targetSizeKey
          );
          break;
        case UpdatePhotoEvents.RESIZE_MULTI_SELECT:
          this.resizeMultiSelectPhotos(
            payload.targetSizeKey,
            payload.selectPhotoImageIds
          );
          break;
        case RemoveSizeEvents.REMOVE:
          this.handleRemoveSize(payload.size_key);
          break;
        case UpdatePhotoEvents.ADVANCED_EDIT:
          this.handleAdvancedEdit(
            payload.imageId,
            payload.sizeKey,
            payload.isLastPhoto
          );
          break;
        case UpdatePhotoEvents.ADVANCED_EDIT_CLOSE:
          this.handleAdvancedEditClose();
          break;
        case WebUI.CART_MERGED:
          this.loadBasketContents(payload.basketPath);
          break;
        case DefaultsEvents.CHANGE_SIZE:
          this.handleDefaultSizeChanged(payload.sizeKey);
          break;
      }
    });
  },
  unloadCallbacks() {
    builderDispatch.unregister(this.builderDispatchToken);
  },
  loadData() {
    const that = this;
    const xhr = new XMLHttpRequest();

    xhr.onreadystatechange = function() {
      if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
        const data = JSON.parse(this.responseText);

        if (!data.sizes.length || !data.finishes.length) {
          return that.showEmptyBuilder(data);
        }

        // set some defaults
        // either from the JSON
        // or querystring

        const {
          size: defaultSizeParam,
          finish: defaultFinishParam,
          delivery: defaultDeliveryOption,
        } = parseQueryString();
        const availableFinishesByDeliveryOption = generateAvailableFinishesByDeliveryOption(
          data
        );

        let initialFinish;
        let initialDeliveryOption;
        let defaultSize;
        let param;

        // find default finish according to querystring
        if (defaultFinishParam) {
          initialFinish = data.finishes.find(
            finish =>
              finish.title.toLowerCase() === defaultFinishParam.toLowerCase()
          );
        }

        // find default size and finish according to querystring
        if (defaultSizeParam) {
          if (initialFinish) {
            defaultSize = data.sizes.find(
              size =>
                size.size_id.toLowerCase() === defaultSizeParam.toLowerCase() &&
                size.options.finish.name === initialFinish.name
            );
          } else {
            defaultSize = data.sizes.find(
              size =>
                size.size_id.toLowerCase() === defaultSizeParam.toLowerCase()
            );
          }
        }

        if (initialFinish?.name && !defaultSize) {
          const initialFinishName = initialFinish.name.toLowerCase();
          defaultSize = data.sizes.find(({ options }) =>
            initialFinishName.includes(options.finish.name)
          );
        }

        if (!defaultSize) {
          defaultSize = SizeHelpers.defaultSize(data.sizes) || data.sizes[0];
        }

        // find default delivery according to querystring
        param = (defaultDeliveryOption || '').toLowerCase();
        // default to first time if not already set
        if (!param) {
          param = data.delivery_options[0].key;
        }

        // now then, the default delivery option should be subordinate to the size and finish chosen
        // make sure the delivery option key is within the size's options
        // if it isn't, in that case default to the first delivery option of the size
        param = parseInt(param, 10);
        if (
          initialFinish &&
          defaultSize.options.finish.name === initialFinish.name &&
          defaultSize.options.delivery_options &&
          defaultSize.options.delivery_options.indexOf(param) === -1
        ) {
          param = defaultSize.options.delivery_options[0];
        }

        initialDeliveryOption = data.delivery_options.find(
          delivery_option => delivery_option.key == param
        );

        // or fall back to the first available
        initialDeliveryOption ||
          ([initialDeliveryOption] = data.delivery_options);

        // or fall back to the first available finish with delivery options
        let loopCounter = 1;
        while (!initialFinish) {
          const availableFinishes =
            availableFinishesByDeliveryOption[initialDeliveryOption.key];
          if (availableFinishes) {
            [initialFinish] = availableFinishes;
          } else {
            // there were no availableFinishes for the selected delivery option
            // roll on to the next delivery option and try again.
            const deliveryOptionIndex = data.delivery_options.indexOf(
              initialDeliveryOption
            );
            initialDeliveryOption =
              data.delivery_options[
                (deliveryOptionIndex + 1) % data.delivery_options.length
              ];
            if (loopCounter > data.delivery_options.length) {
              return that.showEmptyBuilder(data);
            }
            ++loopCounter;
            // in theory this could result in a infinite loop
            // but config would have to be pretty screwed up for that to happen.
            // but during tests everything is possible! My computer has self-restarted because of this ;(
            // so I added this 'loopCounter' stuff to avoid infinite loop. (MK)
          }
        }

        that.data = data;
        that.selectedSizeKey = defaultSize.key;
        that.initialSizeKey = defaultSize.key; // We may need to reset to this later
        that.availableFinishesByDeliveryOption = availableFinishesByDeliveryOption;
        that.setState(
          {
            finish: initialFinish,
            delivery_option: initialDeliveryOption,
            viewingSizeKey:
              (defaultSizeParam && defaultSize.key) ||
              SizeHelpers.ALL_PHOTOS_KEY,
            sizes: data.sizes.map(size => ({ ...size, photos: [] })),
          },
          () => {
            delete that.data.sizes;
            that.loadBasketContents();
          }
        );
      }
    };
    xhr.open('GET', jsonDataURL(this.props.urls.data));
    xhr.send();

    function generateAvailableFinishesByDeliveryOption(data) {
      const finishes = {};
      data.finishes.forEach(finish => {
        data.sizes.forEach(size => {
          size.options.delivery_options.forEach(delivery_option_id => {
            if (finish.name === size.options.finish.name) {
              finishes[delivery_option_id] ||
                (finishes[delivery_option_id] = []);
              if (finishes[delivery_option_id].indexOf(finish) === -1) {
                finishes[delivery_option_id].push(finish);
              }
            }
          });
        });
      });
      return finishes;
    }
  },

  loadBasketContents(basketUrl) {
    const that = this;
    const xhr = new XMLHttpRequest();

    xhr.onreadystatechange = function() {
      if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
        const { prints = [] } = JSON.parse(this.responseText);
        const validFinishNames = that.data.finishes.map(finish => finish.name);

        // add photos to correct size
        prints.forEach(function(print) {
          print.fromBasket = true;

          // eject if this print isn't of a valid finish
          if (validFinishNames.indexOf(print.finish) === -1) {
            return;
          }

          const sizeHash = {};
          let size = sizeHash[print.size];

          // hopefully the size is already present in the lookup hash
          if (!size) {
            // it wasn't, but it might be in the existing sizes
            size = this.state.sizes.reduce((memo, size) => {
              const regex = new RegExp(`${print.size}.+${print.finish}.+${print.border}$`);
              return size.key.match(regex) ? size : memo;
            }, null);
          }

          if (size.photos.every(p => p.image_id !== print.image_id)) {
            size.photos.push(print);

            sizeHash[print.size] = size;
            delete print.size; // was needed for reconciling, don't need it anymore
          }
        }, that);

        let initialDeliveryOption;
        let initialFinish;
        let invalidDeliveryOptionSelected = false;

        if (prints[0]) {
          const { delivery, finish: printFinish } = prints[0];
          initialFinish = window.features.allowMultiplePrintFinishesPerOrder
            ? that.state.finish
            : that.data.finishes.find(finish => finish.name === printFinish);
          initialDeliveryOption = that.data.delivery_options.find(
            delivery_option =>
              that.availableFinishesByDeliveryOption[delivery_option.key] &&
              delivery_option.key == delivery
          );

          invalidDeliveryOptionSelected = that.availableFinishesByDeliveryOption[
            initialDeliveryOption.key
          ]?.every(f => f.key !== initialFinish.key);
        }

        const initialSize =
          !that.desktop && that.determineInitialSize(that.state.viewingSizeKey);

        that.setState(
          {
            finish: initialFinish || that.state.finish,
            delivery_option:
              initialDeliveryOption || that.state.delivery_option,
            viewingSizeKey: initialSize && initialSize.key,
            ready: true,
            invalidDeliveryOptionSelected,
          },
          function() {
            if (basketUrl) {
              return;
            }

            // if builder has no prints then fire loader
            if (this.props.uploadFromGalleryListenerUrl) {
              this.handleAddPhotosFromGallery();
              return;
            }

            if (!prints.length) {
              this.handleAddPhotos();
            }

            if (invalidDeliveryOptionSelected) {
              const nextAvailableDeliveryOption = this.data.delivery_options.find(
                deliveryOption =>
                  that.availableFinishesByDeliveryOption[deliveryOption.key]
              );
              const { finish } = this.state;

              builderDispatch.dispatch({
                type: UpdatePhotoEvents.SHOW_INVALID_DELIVERY_OPTION_MODAL,
                nextDeliveryOption: nextAvailableDeliveryOption,
                printFinish: finish,
                onContinue: () =>
                  this.setDeliveryAndFinish(
                    nextAvailableDeliveryOption,
                    finish
                  ),
              });
            }
          }
        );
      }
    };
    xhr.open(
      'GET',
      `${basketUrl || this.props.urls.basket}.json` + `?_=${Date.now()}`
    );
    xhr.send();
  },

  allPhotos() {
    return this.state.sizes.reduce(
      (memo, size) => memo.concat(size.photos),
      []
    );
  },
  hasPrints() {
    return this.state.sizes.some(SizeHelpers.sizeHasPrints);
  },
  hasPhotos() {
    return this.state.sizes.some(SizeHelpers.sizeHasPhotos);
  },
  hasPhotosForSelectedFinish() {
    return this.getSizesByFinish().some(SizeHelpers.sizeHasPhotos);
  },
  totalQuantity() {
    return this.allPhotos().reduce((total, photo) => total + photo.quantity, 0);
  },
  getSizeFromKey(sizeKey) {
    return this.state.sizes.find(size => size.key == sizeKey);
  },
  getPhoto(size, imageId, fallbackToRootImageIdOrOriginalImageId = false) {
    const photos = size && size.photos ? size.photos : this.allPhotos();
    // Return the photo for which one of its image id(s) matches the supplied imageId
    // There should only be one match; hence the use of Array.prototype.find()
    return photos.find(
      photo =>
        photo.image_id == imageId ||
        (fallbackToRootImageIdOrOriginalImageId &&
          (photo.root_image_id == imageId ||
            photo.original_image_id == imageId))
    );
  },
  getPhotosBySize(size) {
    return this.state.sizes.find(s => s.key === size).photos;
  },
  getSizesByFinish() {
    const { sizes, finish } = this.state;

    return sizes.filter(size => {
      const regex = new RegExp(`${finish.name}`);

      return size.key.match(regex);
    });
  },
  getPhotosWithTheSameRootId(rootImageId) {
    return this.allPhotos().filter(photo => photo.root_image_id == rootImageId);
  },
  availableSizesForSelectedDeliveryAndFinish() {
    const { finish, delivery_option } = this.state;
    return this.availableSizesForDeliveryAndFinish(delivery_option, finish);
  },
  availableSizesForDeliveryAndFinish(delivery = {}, finish = {}) {
    return this.state.sizes.filter(
      size =>
        size.options.finish.name == finish.name &&
        (size.options.delivery_options || []).includes(delivery.key)
    );
  },
  handleViewingSizeChanged(sizeKey) {
    this.setState({
      viewingSizeKey: sizeKey,
    });
  },
  handleResize(targetSizeKey) {
    const { viewingSizeKey } = this.state;
    const resizeAll = viewingSizeKey === SizeHelpers.ALL_PHOTOS_KEY;

    const sizes = resizeAll
      ? this._resizeAll(targetSizeKey)
      : this._resizeByKey(targetSizeKey, viewingSizeKey);

    this.setState({ sizes }, function() {
      this.handleViewingSizeChanged(targetSizeKey);
      this.save();
    });
  },
  resizeMultiSelectPhotos(targetSizeKey, selectPhotoImageIds) {
    const photoSizes = [...this.state.sizes];

    // Make a copy of all the selected photos
    const selectedPhotosToMove = photoSizes.flatMap(({ photos }) =>
      photos.filter(photo => selectPhotoImageIds.includes(photo.image_id))
    );

    const sizes = photoSizes.map(size => {
      // Remove selected images from old size
      size.photos = size.photos.filter(
        photo => !selectPhotoImageIds.includes(photo.image_id)
      );

      // Add selected photo to new size
      if (size.key === targetSizeKey) {
        size.photos = new PhotoModifications(
          this._mergePhotosAndUpdateQuantity([
            ...size.photos,
            ...selectedPhotosToMove,
          ])
        ).touch();
      }

      return size;
    });

    this.setState({ sizes }, () => {
      this.handleViewingSizeChanged(SizeHelpers.ALL_PHOTOS_KEY);
      this.save();
    });
  },
  _resizeByKey(targetSizeKey, viewingSizeKey) {
    // Merge the photos from the current viewing size
    // with those from the target size
    const photos = this._mergePhotosAndUpdateQuantity([
      ...this.getPhotosBySize(viewingSizeKey),
      ...this.getPhotosBySize(targetSizeKey),
    ]);

    return this.state.sizes.map(size => {
      // Remove all the source photos
      if (size.key === viewingSizeKey) {
        size.photos = [];
      }
      // Merge the source and destination photos
      if (size.key === targetSizeKey) {
        size.photos = new PhotoModifications(photos).touch();
      }

      return size;
    });
  },
  _resizeAll(targetSizeKey) {
    const sizePhotos = this._mergePhotosAndUpdateQuantity(
      flatMap(this.getSizesByFinish(), size => size.photos)
    );

    return this.state.sizes.map(size => {
      // If the key matches the target and finish type then assign all the
      // photos to it, otherwise make the array empty. This
      // will move every single photo from every single size to
      // a single size

      const targetFinish = targetSizeKey.split(';').pop();
      let photos;

      if (size.key === targetSizeKey) {
        photos = new PhotoModifications(sizePhotos).touch();
      } else if (size.key.includes(targetFinish)) {
        photos = [];
      } else {
        photos = size.photos;
      }

      return {
        ...size,
        photos,
      };
    });
  },
  _mergePhotosAndUpdateQuantity(photos) {
    // Take a flattened list of photos and combine those
    // with the same root id. Duplicate photos will have
    // their quantities increased
    return photos.reduce((acc, photo) => {
      const dupeIndex = acc.findIndex(
        s => s.root_image_id === photo.root_image_id
      );
      // This photo is safe to add
      if (dupeIndex === -1) return [...acc, photo];
      // Update the quantity if it's a dupe, and mark the
      // now unused image for deletion
      new PhotoModifications(acc[dupeIndex]).addTo('quantity', photo.quantity);
      this._markForDeletion(photo.id);

      return acc;
    }, []);
  },
  _markForDeletion(id) {
    const deleted = this.toBeDeleted || [];
    this.toBeDeleted = [...deleted, id];
  },
  handleQuantityChanged(quantity, sizeKey) {
    quantity = parseInt(quantity) || 1;
    sizeKey = sizeKey || this.state.viewingSizeKey;

    const sizes = [...this.state.sizes];

    sizes
      .filter(size => size.key == sizeKey)
      .forEach(size =>
        new PhotoModifications(size.photos).set('quantity', quantity)
      );

    this.setState(
      {
        sizes,
      },
      () => {
        this.save();
      }
    );
  },
  handleOptionsChanged({
    finish = this.state.finish,
    delivery_option = this.state.delivery_option,
  } = {}) {
    const sizesWithPrints = this.state.sizes.filter(SizeHelpers.sizeHasPrints);
    const availableSizes = this.availableSizesForDeliveryAndFinish(
      delivery_option,
      finish
    ).map(size => size.key);

    const invalidSizes = sizesWithPrints.filter(size => {
      const keyWithoutFinish = size.key.split(';', 2).join(';');
      return !availableSizes.some(
        availableSize =>
          availableSize.match(keyWithoutFinish) &&
          size.options.delivery_options.includes(delivery_option.key)
      );
    });

    if (invalidSizes.length) {
      const invalidKeySizes = invalidSizes.map(size => size.key);

      builderDispatch.dispatch({
        type: UpdatePhotoEvents.SHOW_INVALID_SIZES_MODAL,
        invalidSizes,
        onContinue: () => {
          const validSizesWithPrints = sizesWithPrints.filter(
            ({ key }) => !invalidKeySizes.includes(key)
          );

          this.setDeliveryAndFinishAndRemoveInvalidSizes(
            delivery_option,
            finish,
            invalidKeySizes
          );
          this._applyFinishToSizesPrints(validSizesWithPrints, finish);

          if (
            !ViewingSizeKey.validForSizesWithPrints(
              validSizesWithPrints,
              this.state.viewingSizeKey
            )
          ) {
            this.handleViewingSizeChanged(SizeHelpers.ALL_PHOTOS_KEY);
          }
        },
      });
    } else {
      this._applyFinishToSizesPrints(sizesWithPrints, finish);
      this.setDeliveryAndFinish(delivery_option, finish);
    }
  },
  setDeliveryAndFinishAndRemoveInvalidSizes(
    delivery_option,
    finish,
    invalidSizeKeys = []
  ) {
    invalidSizeKeys.forEach(invalidSizeKey => {
      const size = this.getSizeFromKey(invalidSizeKey);
      new PhotoModifications(size.photos).set('quantity', 0);
    });

    this.setState(
      {
        sizes: [...this.state.sizes],
        finish,
        delivery_option,
      },
      function() {
        this.ignoreModified = true;
        this.save();
      }
    );
  },
  setDeliveryAndFinish(delivery_option, finish) {
    this.setState(
      {
        finish,
        delivery_option,
        invalidDeliveryOptionSelected: false,
      },
      function() {
        this.ignoreModified = true;
        this.save();
      }
    );
  },
  handleAdvancedPhotoQuantityChanged(value) {
    value = Math.max(value, 1);

    const sizes = [...this.state.sizes];

    sizes
      .filter(size => size.key == this.advanced_edit.size.key)
      .forEach(size => {
        const photos = size.photos.filter(
          photo => photo.id == this.advanced_edit.photo.id
        );
        new PhotoModifications(photos).set('quantity', value);
      });

    this.setState(
      {
        sizes,
      },
      function() {
        this.save();
      }
    );
  },
  handleAdvancedEditSelectPhoto(photo) {
    if (!this.advanced_edit) return;

    this.advanced_edit.size = photo.size;
    this.advanced_edit.photo = photo;
  },
  handleApplyEditPhoto(updatedPhotos, save) {
    updatedPhotos = [].concat(updatedPhotos);

    let { viewingSizeKey } = this.state;

    updatedPhotos.forEach(updatedPhoto => {
      const size = this.getSizeFromKey(updatedPhoto.size.key);
      const photo =
        this.getPhoto(size, updatedPhoto.image_id) ||
        this.getPhoto(size, updatedPhoto.root_image_id, true);

      if (photo) {
        new PhotoModifications(photo).setFrom(
          updatedPhoto,
          ...PHOTO_ATTRIBUTES
        );
      }

      viewingSizeKey =
        viewingSizeKey != size.key || SizeHelpers.sizeHasPrints(size)
          ? viewingSizeKey
          : undefined;
    });

    this.setState(
      ({ sizes }) => {
        return {
          sizes: [...sizes],
          viewingSizeKey,
        };
      },
      () => {
        if (save) {
          this.save();
        }
      }
    );
  },
  _applyFinishToSizesPrints(sizesWithPrints = [], finish = {}) {
    if (window.features.allowMultiplePrintFinishesPerOrder) {
      return;
    }

    const { name } = finish;

    if (!name || !sizesWithPrints.length) {
      return;
    }

    const { sizes, viewingSizeKey } = this.state;
    const allSizes = cloneDeep(sizes);

    // Same method as handleResizePhoto()
    // however making a copy of state to target photos.

    sizesWithPrints.forEach(({ size_id, photos, key }) => {
      photos.forEach(({ image_id }) => {
        const sourceSize = allSizes.find(size => size.key === key);
        const photo = this.getPhoto(sourceSize, image_id, true);

        const targetSizeKey = new ViewingSizeKey(viewingSizeKey).create({
          size: size_id,
          finish: name,
		  border: photo.border
        });

        const targetSize = allSizes.find(size => size.key === targetSizeKey);
        const photoModifications = new PhotoModifications(photo);

        if (sourceSize.key === targetSize.key) {
          // Will only match when the delivery option is going to change
          // So no need to move photos
          return;
        }

        // if there's a version of this photo lying in the target size then remove it
        let index = targetSize.photos.reduce(
          (memo, targetPhoto, i) =>
            targetPhoto.root_image_id == photo.root_image_id ? i : memo,
          -1
        );

        if (index > -1) {
          const toBeDeleted = targetSize.photos.splice(index, 1);
          this.toBeDeleted = (this.toBeDeleted || []).concat(
            toBeDeleted.map(photo => photo.id)
          );
        }

        // remove this photo from the source size
        index = sourceSize.photos.indexOf(photo);
        if (index > -1) {
          photoModifications.touch();
          sourceSize.photos.splice(index, 1);
        }

        // // add it to the target size
        targetSize.photos.push(photo);
      });
    });

    if (viewingSizeKey) {
      const newTargetSizeKey = new ViewingSizeKey(viewingSizeKey).update(
        'finish',
        name
      );
      this.handleViewingSizeChanged(newTargetSizeKey);
    }

    this.setState({
      sizes: [...allSizes],
    });
  },
  handleResizePhoto(sourceSizeKey, imageId, targetSizeKey) {
    const sourceSize = this.getSizeFromKey(sourceSizeKey);
    const photo = this.getPhoto(sourceSize, imageId, true);
    const targetSize = this.getSizeFromKey(targetSizeKey);
    const photoModifications = new PhotoModifications(photo);

    photoModifications.reset('crop');

    // if there's a version of this photo lying in the target size then remove it
    let index = targetSize.photos.reduce(
      (memo, targetPhoto, i) =>
        targetPhoto.root_image_id == photo.root_image_id ? i : memo,
      -1
    );

    if (index > -1) {
      const toBeDeleted = targetSize.photos.splice(index, 1);
      this.toBeDeleted = (this.toBeDeleted || []).concat(
        toBeDeleted.map(photo => photo.id)
      );
    }

    // remove this photo from the source size
    index = sourceSize.photos.indexOf(photo);
    if (index > -1) {
      photoModifications.touch();
      sourceSize.photos.splice(index, 1);
    }

    // add it to the target size
    targetSize.photos.unshift(photo);

    this.setState(
      {
        sizes: [...this.state.sizes],
        viewingSizeKey: this.state.viewingSizeKey && targetSizeKey,
      },
      function() {
        this.refreshEL2(targetSizeKey, photo.image_id);
        this.save();
      }
    );
  },
  handleAddPhotoToSize(
    sourceSizeKey,
    rootImageIdOrIds,
    targetSizeKey,
    quantity
  ) {
    rootImageIdOrIds = [].concat(rootImageIdOrIds);

    const targetSize = this.getSizeFromKey(targetSizeKey);
    const sourcePhotos = [];

    rootImageIdOrIds.forEach(rootImageId => {
      let newPhoto;

      // Pick first (or any of the returned images as we'll have to reset edits anyway)
      const [sourcePhoto] = this.getPhotosWithTheSameRootId(rootImageId);

      if (sourcePhoto) {
        // 'new photo' may already be in the size but with quantity zero
        newPhoto = targetSize.photos.find(
          photo => photo.root_image_id == rootImageId
        );

        // photo might already in the size array, perhaps with quantity zero
        if (!newPhoto) {
          newPhoto = {};

          // set the newPhoto's Item id to null
          // to dissociate it with the old Item
          new PhotoModifications(newPhoto).setFrom({
            ...sourcePhoto,
            id: null,
            original_image_id: sourcePhoto.image_id,
            make_new_copy: true,
          });

          resetPhotoEdits(newPhoto);

          targetSize.photos.push(newPhoto);
        }

        // Reset Edits only if quantity is 0. This avoids bulk resetting.
        if (newPhoto.quantity === 0) {
          resetPhotoEdits(newPhoto);
        }
        new PhotoModifications(newPhoto).set('quantity', quantity || 1);
        sourcePhotos.push(sourcePhoto);
      }
    });

    this.setState(
      ({ sizes, viewingSizeKey }) => {
        return {
          sizes: [...sizes],
          viewingSizeKey: this.advanced_edit ? targetSizeKey : viewingSizeKey,
        };
      },
      () => {
        this.saveHard(() => {
          const sourcePhoto = sourcePhotos[0];
          const newPhotoImageId =
            sourcePhoto &&
            targetSize.photos.reduce(
              (memo, photo) =>
                sourcePhoto.root_image_id == photo.root_image_id
                  ? photo.image_id
                  : memo,
              null
            );

          if (this.advanced_edit && newPhotoImageId) {
            this.refreshEL2(targetSizeKey, newPhotoImageId);
          }

          this.setState({ selectedPhotoId: newPhotoImageId });
        });
      }
    );
  },
  handleRemovePhoto(sizeKey, imageIdOrIds, selectedPhotoId) {
    imageIdOrIds = [].concat(imageIdOrIds);

    const size = this.getSizeFromKey(sizeKey);

    if (this.allowZeroQuantity()) {
      imageIdOrIds.forEach(imageId => {
        const photo = this.getPhoto(size, imageId, true);
        if (!photo) {
          return;
        }

        new PhotoModifications(photo).set('quantity', 0);
      });
    } else {
      this.deletePhotos(sizeKey, imageIdOrIds);
    }

    this.setState(
      ({ sizes, viewingSizeKey }) => {
        return {
          sizes: [...sizes],
          selectedPhotoId,
          viewingSizeKey:
            viewingSizeKey !== size.key || SizeHelpers.sizeHasPrints(size)
              ? viewingSizeKey
              : undefined,
        };
      },
      () => {
        this.advancedEditor().close(true);
        this.save();
      }
    );
  },
  handleRemovePhotoByRoot(rootImageId) {
    let removed = [];

    this.state.sizes.forEach(size => {
      const photo = this.getPhoto(size, rootImageId, true);

      if (photo) {
        const { photos } = size;
        const index = photos.indexOf(photo);

        if (index > -1) removed = removed.concat(photos.splice(index, 1));
      }
    });

    this.setState(
      ({ sizes }) => {
        return {
          sizes: [...sizes],
          selectedPhotoId: undefined,
        };
      },
      () => {
        const toBeDeleted = removed
          .filter(photo => !!photo.id)
          .map(photo => photo.id);

        this.toBeDeleted = (this.toBeDeleted || []).concat(toBeDeleted);
        this.save();
      }
    );
  },
  handleRemoveSize(sizeKey) {
    const size = this.getSizeFromKey(sizeKey);

    // Set photo quantity to handle 0 quantity
    new PhotoModifications(size.photos).set('quantity', 0);

    this.setState(
      {
        sizes: [...this.state.sizes],
        viewingSizeKey: undefined,
      },
      function() {
        this.save();
      }
    );
  },
  handleDefaultSizeChanged(sizeKey) {
    this.selectedSizeKey = sizeKey;

    this.setState({
      viewingSizeKey: sizeKey,
    });
  },
  handleAdvancedEditClose() {
    delete this.advanced_edit;
  },
  deletePhotos(sizeKey, imageIdOrIds) {
    imageIdOrIds = [].concat(imageIdOrIds);

    const size = this.getSizeFromKey(sizeKey);
    if (!size) return;

    const { photos } = size;
    const removed = [];

    imageIdOrIds.forEach(imageId => {
      const photo = this.getPhoto(size, imageId, true);
      const index = photos.indexOf(photo);

      if (index > -1) {
        removed.push(...photos.splice(index, 1));
      }
    });

    this.toBeDeleted = (this.toBeDeleted || []).concat(removed.map(p => p.id));
    this.save();
  },
  save() {
    this.debouncedSave ||
      (this.debouncedSave = genericHelpers.debounce(() => {
        this.saveHard(() => {
          this.clearWarningBeforeNavigatingAwayPendingSave();
        });
      }, 2000));

    this.setWarningBeforeNavigatingAwayPendingSave();
    this.debouncedSave();
  },
  saveHard(onComplete) {
    if (this.saving && !onComplete) {
      return;
    }
    const allPhotos = this.allPhotos();
    const data = {
      sizes: this.state.sizes
        .filter(SizeHelpers.sizeHasPrints)
        .map(buildSizeData, this)
        .filter(size => size.photos.length),
      to_be_deleted: []
        .concat(this.toBeDeleted, photosWithNoQuantity())
        .filter(Boolean),
    };

    ['sizes', 'to_be_deleted'].forEach(key => {
      if (data[key].length === 0) {
        delete data[key];
      }
    });

    const xhr = new XMLHttpRequest();
    xhr.open('POST', this.props.urls.add_to_cart);
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
        // we've been returned the item ids
        // of the new files which we will need to use when deleting them
        const newItems = JSON.parse(xhr.responseText);
        newItems.forEach(setIdsOfNewItem, this);
        this.saving = false;
        onComplete && onComplete();
      }

      function setIdsOfNewItem(newItem) {
        const size = this.getSizeFromKey(newItem.size);
        const photo = this.getPhoto(size, newItem.original_image_id);

        if (photo) {
          photo.id = newItem.id;
          photo.image_id = newItem.image_id;
        }
      }
    };

    if (Object.keys(data).length) {
      xhr.send(JSON.stringify(data));
    } else {
      onComplete && onComplete();
    }

    this.ignoreModified = false;
    this.toBeDeleted = [];
    new PhotoModifications(allPhotos).clear();
    this.saving = true;

    function photosWithNoQuantity() {
      return allPhotos
        .filter(photo => photo.quantity < 1)
        .map(photo => photo.id);
    }

    function buildSizeData(size) {
      const {
        delivery_option: { key: deliveryOption },
      } = this.state;

      const photos = size.photos.filter(printFilter, this).map(buildPhotoData);
      const what = size.options.key;

      return {
        photos,
        what,
      };

      function printFilter({ id, image_id: imageId, quantity, modified }) {
        return (
          !id ||
          (typeof imageId === 'number' &&
            quantity &&
            (this.ignoreModified || modified))
        );
      }

      function buildPhotoData(photo) {
        return {
          id: photo.id,
          image_id: photo.image_id,
          make_new_copy: !!photo.make_new_copy,
          quantity: photo.quantity,
          filename: photo.filename,
          filesize: photo.filesize,
          angle: photo.angle,
          crop: photo.crop,
          fx: photo.fx,
          size_key: size.key,
          saturation: photo.saturation,
          colour_fx: photo.colour_fx,
          straighten: photo.straighten,
          gamma: photo.gamma,
          delivery_option: deliveryOption,
        };
      }
    }
  },
  showEmptyBuilder(data) {
    this.data = data;
    this.setState(
      {
        finish: {},
        delivery_option: data.delivery_options[0] || {},
        viewingSizeKey: SizeHelpers.ALL_PHOTOS_KEY,
        sizes: [],
      },
      () => {
        alert(
          'No available finishes or sizes for the given product. Please report this problem.'
        );
      }
    );
  },
  determineInitialSize(key) {
    if (!key) {
      return { key: SizeHelpers.ALL_PHOTOS_KEY };
    }

    const defaultSize = this.getSizeFromKey(key);
    // If there are photos for the default size
    // then show them, otherwise show everything
    return defaultSize.photos.length > 0
      ? defaultSize
      : { key: SizeHelpers.ALL_PHOTOS_KEY };
  },
  advancedEditor() {
    return this.advancedEditorRef
      ? this.advancedEditorRef.component
      : {
          setData() {},
          close() {},
        };
  },
  handleUploadClosed() {
    if (this.addPhotosButtonRef) {
      this.addPhotosButtonRef.focus();
    }
  },
  handleAddToCart(callback) {
    this.saveHard(() => {
      this.clearWarningBeforeNavigatingAwayPendingSave();

      if (typeof callback === 'function') callback();
    });
  },
  setWarningBeforeNavigatingAwayPendingSave() {
    this.setBeforeUnload('Changes that you made may not be saved.');
  },
  clearWarningBeforeNavigatingAwayPendingSave() {
    this.setBeforeUnload();
  },
};

export default PrintsBuilderMixin;
